// Secure Logging Utility for Production
// This utility ensures sensitive information is not exposed in production logs

const isProduction = process.env.NODE_ENV === 'production' || 
                     process.env.REACT_APP_ENVIRONMENT === 'production';

// Sensitive data patterns to hide
const SENSITIVE_PATTERNS = [
  /api[_-]?key["\s]*[:=]["\s]*[^"\s,}]+/gi,
  /token["\s]*[:=]["\s]*[^"\s,}]+/gi,
  /secret["\s]*[:=]["\s]*[^"\s,}]+/gi,
  /password["\s]*[:=]["\s]*[^"\s,}]+/gi,
  /key["\s]*[:=]["\s]*[^"\s,}]+/gi,
  /aws_access_key_id["\s]*[:=]["\s]*[^"\s,}]+/gi,
  /aws_secret_access_key["\s]*[:=]["\s]*[^"\s,}]+/gi,
  /email/gi,
  /phone/gi,
  /address/gi,
  /jwt/gi,
  /id_token/gi
];

// Sensitive keys that should be completely hidden
const SENSITIVE_KEYS = [
  'api_key', 'apiKey', 'apikey',
  'token', 'id_token', 'idToken', 'access_token', 'accessToken',
  'secret', 'password', 'pwd', 'pass',
  'jwt', 'bearer',
  'key', 'secretKey', 'privateKey',
  'aws_access_key_id', 'aws_secret_access_key',
  'baseCost', 'base_cost', 'originalCost', 'original_cost',
  'cost', 'printifyCost', 'printify_cost',
  'markup', 'markupMultiplier', 'markup_multiplier',
  'email', 'emailAddress', 'email_address',
  'phone', 'phoneNumber', 'phone_number',
  'address', 'streetAddress', 'street_address',
  'userData', 'user_data', 'userInfo', 'user_info'
];

// Pricing fields that should show purchase price only, not original cost
const PRICING_FIELDS = [
  'cost', 'baseCost', 'base_cost', 'originalCost', 'original_cost',
  'printifyCost', 'printify_cost', 'variant_price', 'product_cost'
];

// Sanitize sensitive data from strings
const sanitizeString = (str) => {
  if (typeof str !== 'string') return str;
  
  let sanitized = str;
  SENSITIVE_PATTERNS.forEach(pattern => {
    sanitized = sanitized.replace(pattern, (match) => {
      const key = match.split(/[:=]/)[0].trim();
      return `${key}: [HIDDEN]`;
    });
  });
  
  return sanitized;
};

// Convert cost to purchase price (apply markup if needed)
const convertCostToPurchasePrice = (cost) => {
  if (typeof cost !== 'number' || isNaN(cost)) return '[PRICE_HIDDEN]';
  // Apply 35% markup (same as MARKUP_MULTIPLIER in pricingService.js)
  const purchasePrice = Math.round(cost * 1.35);
  return purchasePrice;
};

// Sanitize objects recursively
const sanitizeObject = (obj, seen = new WeakSet()) => {
  if (obj === null || obj === undefined) return obj;
  
  // Prevent circular references
  if (typeof obj === 'object' && seen.has(obj)) {
    return '[Circular Reference]';
  }
  
  if (typeof obj === 'string') {
    return sanitizeString(obj);
  }
  
  if (Array.isArray(obj)) {
    seen.add(obj);
    return obj.map(item => sanitizeObject(item, seen));
  }
  
  if (typeof obj === 'object') {
    seen.add(obj);
    const sanitized = {};
    for (const [key, value] of Object.entries(obj)) {
      const lowerKey = key.toLowerCase();
      
      // Hide completely sensitive keys
      if (SENSITIVE_KEYS.some(sensitiveKey => 
        lowerKey === sensitiveKey.toLowerCase() || 
        lowerKey.includes(sensitiveKey.toLowerCase())
      )) {
        sanitized[key] = '[HIDDEN]';
      }
      // Convert cost fields to purchase price
      else if (PRICING_FIELDS.some(pricingField => 
        lowerKey === pricingField.toLowerCase() || 
        lowerKey.includes(pricingField.toLowerCase())
      )) {
        sanitized[key] = typeof value === 'number' ? convertCostToPurchasePrice(value) : '[PRICE_HIDDEN]';
      } else {
        sanitized[key] = sanitizeObject(value, seen);
      }
    }
    return sanitized;
  }
  
  return obj;
};

// Production-safe logging functions that always sanitize sensitive data
// Use originalConsole to avoid infinite recursion
export const secureLog = {
  log: (...args) => {
    try {
      const sanitized = args.map(arg => sanitizeObject(arg));
      if (!isProduction) {
        originalConsole.log('[DEV]', ...sanitized);
      }
    } catch (error) {
      // Fallback to original console if sanitization fails
      originalConsole.error('Secure logging error:', error);
      originalConsole.log(...args);
    }
  },
  
  warn: (...args) => {
    try {
      const sanitized = args.map(arg => sanitizeObject(arg));
      if (isProduction) {
        originalConsole.warn('[PROD]', ...sanitized);
      } else {
        originalConsole.warn('[DEV]', ...sanitized);
      }
    } catch (error) {
      // Fallback to original console if sanitization fails
      originalConsole.error('Secure logging error:', error);
      originalConsole.warn(...args);
    }
  },
  
  error: (...args) => {
    try {
      const sanitized = args.map(arg => sanitizeObject(arg));
      if (isProduction) {
        originalConsole.error('[PROD]', ...sanitized);
      } else {
        originalConsole.error('[DEV]', ...sanitized);
      }
    } catch (error) {
      // Fallback to original console if sanitization fails to prevent infinite loop
      // Use a try-catch to prevent any further recursion
      try {
        originalConsole.error('Secure logging error (fallback):', error);
        originalConsole.error(...args);
      } catch (e) {
        // Last resort: do nothing to prevent infinite recursion
      }
    }
  },
  
  info: (...args) => {
    try {
      const sanitized = args.map(arg => sanitizeObject(arg));
      if (!isProduction) {
        originalConsole.info('[DEV]', ...sanitized);
      }
    } catch (error) {
      // Fallback to original console if sanitization fails
      originalConsole.error('Secure logging error:', error);
      originalConsole.info(...args);
    }
  },
  
  debug: (...args) => {
    try {
      const sanitized = args.map(arg => sanitizeObject(arg));
      if (!isProduction) {
        originalConsole.debug('[DEV]', ...sanitized);
      }
    } catch (error) {
      // Fallback to original console if sanitization fails
      originalConsole.error('Secure logging error:', error);
      originalConsole.debug(...args);
    }
  }
};

// Override console methods AFTER defining secureLog
console.log = secureLog.log;
console.warn = secureLog.warn;
console.error = secureLog.error;
console.info = secureLog.info;
console.debug = secureLog.debug;

// Always override console methods to ensure sensitive data is never logged
// IMPORTANT: Capture original console methods BEFORE overriding
const originalConsole = {
  log: console.log.bind(console),
  warn: console.warn.bind(console),
  error: console.error.bind(console),
  info: console.info.bind(console),
  debug: console.debug.bind(console)
};

// Export original console for emergency debugging if needed
export const originalConsole_ = originalConsole;

export default secureLog;
